/* Pure helpers behind the reusable queue component — ages, the sort
 * modes and the composable filters. Kept out of the .tsx files so those
 * export components only, and so the ordering rules stay unit-readable.
 */

import type { TaskQueueItem } from '../../lib/tasks'
import { blocksRecord } from '../patterns'
import { chaseIsRecommended, isEvidenceChase } from '../chaseGroups'

/** Ruled: an age past 30 days prints red and bold. */
export const AGE_WARN_DAYS = 30
/** The "older than" filter's threshold. */
export const OLDER_THAN_DAYS = 7

export function ageDays(iso: string | null): number | null {
  if (!iso) return null
  return Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000))
}

export function ageLabel(iso: string | null): string {
  if (!iso) return ''
  const ms = Date.now() - new Date(iso).getTime()
  const h = Math.floor(ms / 3_600_000)
  if (h < 1) return `${Math.max(1, Math.floor(ms / 60_000))}m`
  if (h < 48) return `${h}h`
  return `${Math.floor(h / 24)}d`
}

export function isRecommended(t: TaskQueueItem): boolean {
  return isEvidenceChase(t) && chaseIsRecommended(t)
}

export function priorityClass(t: TaskQueueItem): string {
  if (t.priority === 'urgent') return 'urgent'
  if (t.priority === 'high' || blocksRecord(t.task_type, t.priority)) return 'high'
  return ''
}

/** Blockers first (the kept TasksWorkbench rule), recommended chases
 *  always last, then oldest first. */
export function comparePriority(a: TaskQueueItem, b: TaskQueueItem): number {
  const ba = blocksRecord(a.task_type, a.priority)
  const bb = blocksRecord(b.task_type, b.priority)
  if (ba !== bb) return Number(bb) - Number(ba)
  const ra = Number(isRecommended(a))
  const rb = Number(isRecommended(b))
  if (ra !== rb) return ra - rb
  return a.created_at.localeCompare(b.created_at)
}

export type SortMode = 'priority' | 'oldest' | 'newest' | 'type'

export const SORT_LABELS: Record<SortMode, string> = {
  priority: 'priority (blockers first)',
  oldest: 'oldest first',
  newest: 'newest first',
  type: 'by type',
}

export function sortTasks(list: TaskQueueItem[], mode: SortMode): TaskQueueItem[] {
  const rows = [...list]
  if (mode === 'oldest') return rows.sort((a, b) => a.created_at.localeCompare(b.created_at))
  if (mode === 'newest') return rows.sort((a, b) => b.created_at.localeCompare(a.created_at))
  if (mode === 'type')
    return rows.sort((a, b) => a.task_type.localeCompare(b.task_type) || comparePriority(a, b))
  return rows.sort(comparePriority)
}

export interface QueueFilters {
  types: Set<string>
  urgentHigh: boolean
  olderThan7d: boolean
}

/** Filters COMPOSE — every active one narrows the same list. */
export function applyFilters(list: TaskQueueItem[], f: QueueFilters): TaskQueueItem[] {
  return list.filter((t) => {
    if (f.types.size > 0 && !f.types.has(t.task_type)) return false
    if (f.urgentHigh && t.priority !== 'urgent' && t.priority !== 'high') return false
    if (f.olderThan7d && (ageDays(t.created_at) ?? 0) < OLDER_THAN_DAYS) return false
    return true
  })
}
